import { useTerminalsStateRE } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { trpc } from "@/Utils/trpc";
import { sortByOrderRank } from "@/Utils/wasteStreams";
import InfoPopup from "@/components/InfoPopup/InfoPopup";
import { StyledSelect } from "@/components/StyledSelect";
import { Toggle } from "@/components/Toggle";
import { Button } from "@/components/button";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { useAccessParents } from "@/pages/infrastructure/functions";
import { Translate, useTrans } from "@/translations";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { uniqBy } from "lodash";
import { FilterTimeFrameValue } from "server/config-api/index.js";
import { ACCESS_POINT } from "server/coreApi/accessPoints/types.js";

import {
  COLOR_CONFIG,
  COLOR_KEY,
  CUSTOM_COLORS,
  ColorsTable,
  DEFAULT_CONFIG,
} from "./colorsTable";
import { WasteTypeData, useConfigService } from "./useConfigService";
import { WasteStreamsTable } from "./wasteStreamsTable";

type Props = {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
};

// Intentionally run only once before the component is rendered
const timeFilters = createTimeFilters();

export const WasteStreamsBuilding: React.FC<Props> = ({
  hasUnsavedChanges,
  setHasUnsavedChanges,
}: Props) => {
  const { t, language } = useTrans();
  const { accessParents } = useAccessParents();
  const { currentTerminal } = useTerminalsStateRE();
  const {
    terminalWasteTypeConfig: data,
    clientWasteTypeConfig,
    refetchTerminalWasteTypeConfig: refetch,
    terminalConfig,
    isLoadingTerminalConfig,
    updateTerminalConfig,
  } = useConfigService();
  const { wasteTypes: sanityWasteTypes } = useCommonEntitiesStore();

  const defaultTimeFrameOption = useMemo(
    () => ({
      label: t("configLabels:carrotDefault"),
      value: null,
    }),
    [t],
  );

  const timeFrameOptions = useMemo(() => {
    return [
      defaultTimeFrameOption,
      ...timeFilters.options.map(({ option, value }) => ({
        label: t(option),
        value,
      })),
    ];
  }, [t, defaultTimeFrameOption]);

  const [updatedWasteTypes, setUpdatedWasteTypes] = useState<WasteTypeData[]>(
    [],
  );
  const [updatedColors, setUpdatedColors] =
    useState<COLOR_CONFIG>(DEFAULT_CONFIG);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<{
    label: string;
    value: string | null;
  }>(defaultTimeFrameOption);
  const [enableCostForTenants, setEnableCostForTenants] =
    useState<boolean>(false);
  const [useTenantQRs, setUseTenantQRs] = useState<boolean>(false);
  const [includeWasteTypeInTenantQRs, setIncludeWasteTypeInTenantQRs] =
    useState<boolean>(false);

  // Set initial time frame
  useEffect(() => {
    if (isLoadingTerminalConfig) {
      return;
    }
    if (terminalConfig?.defaultTimeFrame) {
      setSelectedTimeFrame(
        timeFrameOptions.find(
          (to) => to.value === terminalConfig.defaultTimeFrame,
        ) || defaultTimeFrameOption,
      );
    } else {
      setSelectedTimeFrame(defaultTimeFrameOption);
    }
    if (typeof terminalConfig?.enableCostForTenants !== "undefined") {
      setEnableCostForTenants(terminalConfig.enableCostForTenants);
    }
    if (typeof terminalConfig?.useTenantQRs !== "undefined") {
      setUseTenantQRs(terminalConfig.useTenantQRs);
    }
    if (typeof terminalConfig?.includeWasteTypeInTenantQRs !== "undefined") {
      setIncludeWasteTypeInTenantQRs(
        terminalConfig.includeWasteTypeInTenantQRs,
      );
    }
  }, [
    isLoadingTerminalConfig,
    terminalConfig,
    timeFrameOptions,
    defaultTimeFrameOption,
  ]);

  useEffect(() => {
    const hasChangedColors = Object.keys(updatedColors).some(
      (c) =>
        (updatedColors[c as COLOR_KEY] || null) !==
        (terminalConfig?.[c as COLOR_KEY] || null),
    );

    const hasChangedWasteTypes = !!updatedWasteTypes.some((wt) => {
      const originalWT = data?.find(
        (d) => d.wasteTypeCode === wt.wasteTypeCode,
      );

      if (!originalWT) {
        return wt.names.some((n) => n.displayName !== "") || wt.color !== "";
      } else {
        return (
          wt.names.some(
            (n) =>
              n.displayName !==
              originalWT.names.find((owt) => owt.locale === n.locale)
                ?.displayName,
          ) ||
          wt.color !== originalWT.color ||
          wt.downstreamHandlingId !== originalWT.downstreamHandlingId ||
          wt.co2factor !== originalWT.co2factor ||
          wt.costs !== originalWT.costs
        );
      }
    });

    const hasChangedCostToggle =
      enableCostForTenants !== terminalConfig?.enableCostForTenants;

    const hasChangedTenantQR = useTenantQRs !== terminalConfig?.useTenantQRs;
    const hasChangedIncludeWasteStreamInQRCodes =
      includeWasteTypeInTenantQRs !==
      terminalConfig?.includeWasteTypeInTenantQRs;

    setHasUnsavedChanges(
      hasChangedColors ||
        hasChangedWasteTypes ||
        hasChangedCostToggle ||
        hasChangedTenantQR ||
        hasChangedIncludeWasteStreamInQRCodes,
    );
  }, [
    terminalConfig,
    data,
    updatedColors,
    updatedWasteTypes,
    setHasUnsavedChanges,
    enableCostForTenants,
    useTenantQRs,
    includeWasteTypeInTenantQRs,
  ]);

  useEffect(() => {
    const updatedColors: { [k in COLOR_KEY]: string } = { ...DEFAULT_CONFIG };

    CUSTOM_COLORS.forEach((color) => {
      updatedColors[color.key] = terminalConfig?.[color.key] || "";
    });

    setUpdatedColors(updatedColors);
  }, [terminalConfig, setUpdatedColors]);

  const { mutate: updateTerminalWasteTypeConfig } =
    trpc.config.updateTerminalWasteTypeConfig.useMutation({
      onSuccess: () => {
        refetch();
        setHasUnsavedChanges(false);
        setUpdatedWasteTypes([]);
      },
    });

  const containers = useMemo(() => {
    return sortByOrderRank(
      uniqBy(
        accessParents?.flatMap((ap) => ap.containers as ACCESS_POINT[]),
        "wasteType.code",
      ),
      sanityWasteTypes || [],
      "wasteType.code",
    );
  }, [accessParents, sanityWasteTypes]);

  const onSave = useCallback(() => {
    if (updatedWasteTypes.length) {
      updateTerminalWasteTypeConfig({
        updates: updatedWasteTypes.map((uwt) => ({
          ...uwt,
          co2factor: uwt.co2factor ? Number(uwt.co2factor) : null,
          costs:
            uwt.costs?.map((c) => ({
              ...c,
              validFrom: new Date(c.validFrom),
              validTo: c.validTo ? new Date(c.validTo) : null,
              costPerUnit: Number(c.costPerUnit),
            })) || [],
          locale: language,
        })),
        terminalId: currentTerminal.id,
      });
    }

    if (
      Object.values(updatedColors).filter(Boolean).length ||
      (!!terminalConfig && Object.values(terminalConfig).filter(Boolean).length)
    ) {
      updateTerminalConfig({
        ...updatedColors,
        terminalId: currentTerminal.id,
        defaultTimeFrame: (selectedTimeFrame?.value ||
          null) as FilterTimeFrameValue | null,
        enableCostForTenants,
        useTenantQRs,
        includeWasteTypeInTenantQRs,
      });
    }
  }, [
    updatedWasteTypes,
    updatedColors,
    terminalConfig,
    updateTerminalWasteTypeConfig,
    currentTerminal?.id,
    language,
    updateTerminalConfig,
    selectedTimeFrame?.value,
    enableCostForTenants,
    useTenantQRs,
    includeWasteTypeInTenantQRs,
  ]);

  const onResetAll = useCallback(() => {
    setUpdatedWasteTypes(
      data?.map((el) => ({
        ...el,
        names: el.names.map((n) => ({ ...n, displayName: "" })),
        color: "",
        downstreamHandlingId: null,
        co2factor: null,
      })) || [],
    );
    setUpdatedColors(DEFAULT_CONFIG);
    setSelectedTimeFrame(defaultTimeFrameOption);
    setHasUnsavedChanges(true);
  }, [
    setUpdatedWasteTypes,
    setHasUnsavedChanges,
    data,
    defaultTimeFrameOption,
  ]);

  const onSelectedTimeFrameChange = useCallback(
    (selectedTimeFrame: { label: string; value: string }) => {
      setSelectedTimeFrame(selectedTimeFrame);
      setHasUnsavedChanges(
        selectedTimeFrame.value !== terminalConfig?.defaultTimeFrame,
      );
    },
    [setHasUnsavedChanges, terminalConfig?.defaultTimeFrame],
  );

  const wasteTypesConfig = useMemo(
    () =>
      containers.map((c) => {
        const wtConfig = data.find((d) => d.wasteTypeCode === c.wasteType.code);
        const clientConfig = clientWasteTypeConfig.find(
          (d) => d.wasteTypeCode === c.wasteType.code,
        );

        const co2Placeholder = clientConfig?.co2factor?.toString() || "";
        const downstreamHandlingPlaceholderId =
          clientConfig?.downstreamHandlingId || "";

        if (wtConfig) {
          return {
            ...wtConfig,
            co2Placeholder,
            downstreamHandlingPlaceholderId,
          };
        } else {
          return {
            names: [],
            color: "",
            wasteTypeCode: c.wasteType.code,
            co2Placeholder,
            downstreamHandlingPlaceholderId,
          };
        }
      }),
    [containers, data, clientWasteTypeConfig],
  );

  return (
    <>
      <div className="mt-4">
        <ul className="ml-2 list-inside list-disc">
          <Translate i18nKey="hints:wasteStreamBuildingConfig">
            <li />
            <li>
              {
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  href="/infrastructure/manage"
                  style={{ textDecoration: "underline" }}
                  rel="noreferrer"
                />
              }
            </li>
            <li />
          </Translate>
        </ul>
      </div>
      <ColorsTable
        updatedColors={updatedColors}
        setUpdatedColors={setUpdatedColors}
        setHasUnsavedChanges={setHasUnsavedChanges}
      />
      <WasteStreamsTable
        containers={containers}
        data={wasteTypesConfig}
        setHasUnsavedChanges={setHasUnsavedChanges}
        updatedWasteTypes={updatedWasteTypes}
        setUpdatedWasteTypes={setUpdatedWasteTypes}
        showCostColumn
      />
      <div className="my-8">
        <h2 className="mb-2 text-xl">{t("configLabels:costEstimate")}</h2>
        <h3 className="font-dm-sans mb-2 w-1/2 text-xs">
          {t("configLabels:costEstimateDescription")}
        </h3>
        <Toggle
          label={t("configLabels:enableCostForTenants")}
          checked={enableCostForTenants}
          onChange={setEnableCostForTenants}
        />
      </div>
      <div className="my-8">
        <h2 className="mb-2 text-xl">{t("configLabels:qrCodes")}</h2>
        <Toggle
          label={t("configLabels:enableTenantQRCodes")}
          checked={useTenantQRs}
          onChange={setUseTenantQRs}
        />
        {useTenantQRs && (
          <div className="my-4">
            <Toggle
              label={
                <div>
                  {t("configLabels:includeWasteStreamInQRCodes")}
                  <InfoPopup
                    id={`includeWasteStreamInQRCodes`}
                    size={15}
                    className="ml-1 inline-block align-baseline"
                    width="350px"
                  >
                    {t("hints:includeWasteStreamInQRCodes")}
                  </InfoPopup>
                </div>
              }
              checked={includeWasteTypeInTenantQRs}
              onChange={setIncludeWasteTypeInTenantQRs}
            />
          </div>
        )}
      </div>
      <div className="my-8">
        <h2 className="text-xl">{t("configLabels:selectDefaultTimeFrame")}</h2>
        <ul className="mt-2 mb-4 ml-2 list-inside list-disc">
          <Translate i18nKey="hints:defaultTimeFrame" components={[<li />]} />
        </ul>
        <div className="flex items-end gap-4">
          <StyledSelect
            menuPlacement="top"
            className="mt-1 w-60 max-w-xs"
            options={timeFrameOptions}
            value={selectedTimeFrame}
            onChange={onSelectedTimeFrameChange}
          />
        </div>
      </div>
      <div className="sticky bottom-0 w-full bg-white pb-4">
        <hr></hr>
        <div className="mt-4 flex justify-between text-sm">
          <Button
            label={t("actions:save")}
            onClick={onSave}
            loading={false}
            disabled={!hasUnsavedChanges}
          />
          <div
            className="mr-4 cursor-pointer self-center underline"
            onClick={onResetAll}
          >
            {t("actions:resetAll")}
          </div>
        </div>
      </div>
    </>
  );
};
